import React, { useState, useEffect } from 'react';
import { Check, Search, SlidersHorizontal, X } from 'lucide-react';
import { SearchQuery, SearchEntityType, SortOrder, SearchFilters } from '../../types/search';
import { searchService } from '../../services/searchService';
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Field,
  Input,
  Select,
  Spinner,
} from '../ui';

interface AdvancedSearchFormProps {
  onSearch: (query: SearchQuery) => void;
  onClear: () => void;
  initialQuery?: SearchQuery;
  isLoading?: boolean;
}

export const AdvancedSearchForm: React.FC<AdvancedSearchFormProps> = ({
  onSearch,
  onClear,
  initialQuery,
  isLoading = false,
}) => {
  const [query, setQuery] = useState(initialQuery?.q || '');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [filters, setFilters] = useState<SearchFilters>({
    entityTypes: initialQuery?.entityTypes || Object.values(SearchEntityType),
    organizationType: initialQuery?.organizationType || '',
    location: initialQuery?.location || '',
    role: initialQuery?.role || '',
    department: initialQuery?.department || '',
    organizationId: initialQuery?.organizationId || '',
    stakeholderId: initialQuery?.stakeholderId || '',
  });
  const [sortBy, setSortBy] = useState(initialQuery?.sortBy || 'relevance');
  const [sortOrder, setSortOrder] = useState(initialQuery?.sortOrder || SortOrder.DESC);

  useEffect(() => {
    if (initialQuery) {
      setQuery(initialQuery.q || '');
      setFilters({
        entityTypes: initialQuery.entityTypes || Object.values(SearchEntityType),
        organizationType: initialQuery.organizationType || '',
        location: initialQuery.location || '',
        role: initialQuery.role || '',
        department: initialQuery.department || '',
        organizationId: initialQuery.organizationId || '',
        stakeholderId: initialQuery.stakeholderId || '',
      });
      setSortBy(initialQuery.sortBy || 'relevance');
      setSortOrder(initialQuery.sortOrder || SortOrder.DESC);
    }
  }, [initialQuery]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();

    const searchQuery: SearchQuery = {
      q: query.trim() || undefined,
      entityTypes: filters.entityTypes.length > 0 ? filters.entityTypes : undefined,
      organizationType: filters.organizationType || undefined,
      location: filters.location || undefined,
      role: filters.role || undefined,
      department: filters.department || undefined,
      organizationId: filters.organizationId || undefined,
      stakeholderId: filters.stakeholderId || undefined,
      sortBy,
      sortOrder,
      includeHighlights: true,
      page: 1,
      limit: 20,
    };

    setShowAdvanced(false);
    onSearch(searchQuery);
  };

  const handleClear = () => {
    setQuery('');
    setFilters({
      entityTypes: Object.values(SearchEntityType),
      organizationType: '',
      location: '',
      role: '',
      department: '',
      organizationId: '',
      stakeholderId: '',
    });
    setSortBy('relevance');
    setSortOrder(SortOrder.DESC);
    setShowAdvanced(false);
    onClear();
  };

  const handleEntityTypeChange = (entityType: SearchEntityType, checked: boolean) => {
    setFilters(prev => ({
      ...prev,
      entityTypes: checked
        ? [...prev.entityTypes, entityType]
        : prev.entityTypes.filter(type => type !== entityType),
    }));
  };

  const hasActiveFilters = () => {
    return (
      filters.organizationType ||
      filters.location ||
      filters.role ||
      filters.department ||
      filters.organizationId ||
      filters.stakeholderId ||
      filters.entityTypes.length !== Object.values(SearchEntityType).length
    );
  };

  const activeFilterCount = [
    filters.organizationType,
    filters.location,
    filters.role,
    filters.department,
    filters.entityTypes.length !== Object.values(SearchEntityType).length,
  ].filter(Boolean).length;

  return (
    <div className="relative w-full font-ui">
      <form onSubmit={handleSubmit} role="search" className="w-full">
        <div className="group relative w-full">
          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-neutral-400 transition-colors group-focus-within:text-ink">
            {isLoading ? (
              <span role="status" aria-label="Searching" className="inline-flex">
                <Spinner className="h-4 w-4" />
              </span>
            ) : (
              <Search className="h-4 w-4" strokeWidth={1.75} />
            )}
          </span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search"
            aria-label="Search"
            enterKeyHint="search"
            className="block h-11 w-full rounded-full border-0 bg-neutral-100 pl-10 pr-[5.5rem] text-[16px] text-ink placeholder:text-neutral-400 transition-shadow focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-600/25 sm:h-10 sm:text-[14px]"
            autoComplete="off"
          />
          <div className="absolute inset-y-0 right-0 flex items-center gap-0.5 pr-1.5">
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear search"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-neutral-400 transition-colors hover:text-ink"
              >
                <X className="h-4 w-4" strokeWidth={1.75} />
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowAdvanced(true)}
              aria-label={activeFilterCount > 0 ? `Filters (${activeFilterCount} active)` : 'Filters'}
              title="Advanced search"
              aria-haspopup="dialog"
              className={`relative inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
                hasActiveFilters() ? 'bg-white text-primary-600 ring-1 ring-neutral-200' : 'text-neutral-500 hover:bg-white hover:text-ink'
              }`}
            >
              <SlidersHorizontal className="h-4 w-4" strokeWidth={1.75} />
              {activeFilterCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary-600 px-1 text-[10px] font-medium tabular-nums text-white">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </form>

      <Dialog
        open={showAdvanced}
        onClose={() => setShowAdvanced(false)}
        labelledBy="advanced-search-title"
        handle
        className="max-h-[92dvh] sm:max-w-lg"
      >
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <DialogHeader
            id="advanced-search-title"
            title="Advanced search"
            onClose={() => setShowAdvanced(false)}
            className="max-sm:pt-1"
          />
          <DialogBody className="space-y-5">
            {/* Entity Types */}
            <Field label="Search in">
              <div className="flex flex-wrap gap-1.5">
                {Object.values(SearchEntityType).map((entityType) => {
                  const isChecked = filters.entityTypes.includes(entityType);
                  return (
                    <button
                      key={entityType}
                      type="button"
                      role="checkbox"
                      aria-checked={isChecked}
                      onClick={() => handleEntityTypeChange(entityType, !isChecked)}
                      className={`inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 sm:h-8 ${
                        isChecked
                          ? 'bg-neutral-100 text-ink hover:bg-neutral-200/70'
                          : 'text-neutral-400 ring-1 ring-inset ring-neutral-200 hover:text-ink'
                      }`}
                    >
                      {isChecked && <Check className="h-3.5 w-3.5" strokeWidth={2} />}
                      {searchService.getEntityTypePluralName(entityType)}
                    </button>
                  );
                })}
              </div>
            </Field>

            {/* Filter Fields */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Organization Type" htmlFor="adv-org-type">
                <Input
                  id="adv-org-type"
                  value={filters.organizationType || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, organizationType: e.target.value }))}
                  placeholder="e.g., Government, NGO"
                />
              </Field>
              <Field label="Location" htmlFor="adv-location">
                <Input
                  id="adv-location"
                  value={filters.location || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="e.g., New York, London"
                />
              </Field>
              <Field label="Role" htmlFor="adv-role">
                <Input
                  id="adv-role"
                  value={filters.role || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
                  placeholder="e.g., Director, Manager"
                />
              </Field>
              <Field label="Department" htmlFor="adv-department">
                <Input
                  id="adv-department"
                  value={filters.department || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, department: e.target.value }))}
                  placeholder="e.g., IT, Finance"
                />
              </Field>
            </div>

            {/* Sort */}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Sort by" htmlFor="adv-sort-by">
                <Select id="adv-sort-by" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="relevance">Relevance</option>
                  <option value="name">Name</option>
                  <option value="createdAt">Date Created</option>
                  <option value="updatedAt">Date Modified</option>
                </Select>
              </Field>
              <Field label="Order" htmlFor="adv-sort-order">
                <Select id="adv-sort-order" value={sortOrder} onChange={(e) => setSortOrder(e.target.value as SortOrder)}>
                  <option value={SortOrder.DESC}>Descending</option>
                  <option value={SortOrder.ASC}>Ascending</option>
                </Select>
              </Field>
            </div>
          </DialogBody>

          <DialogFooter>
            {hasActiveFilters() && (
              <Button variant="ghost" onClick={handleClear} className="mr-auto h-11 sm:h-9">
                Clear
              </Button>
            )}
            <Button variant="secondary" onClick={() => setShowAdvanced(false)} className="h-11 flex-1 sm:h-9 sm:flex-none">
              Cancel
            </Button>
            <Button type="submit" variant="primary" className="h-11 flex-1 sm:h-9 sm:flex-none">
              Apply
            </Button>
          </DialogFooter>
        </form>
      </Dialog>
    </div>
  );
};
